import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, UserCircle } from 'lucide-react';
import { useProfileStore, type EmployeeProfile } from '../../../store/profileStore';
import { PersonalDetailsForm } from '../../../components/PersonalDetailsForm';
import { EmptyState } from '../../../components/EmptyState';

/** The signed-in employee's own details: read their employment facts, edit their personal info. */
export function MyProfile() {
  const { getSelf, saveSelf } = useProfileStore();
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getSelf();
        if (!cancelled) setProfile(p);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || 'Could not load your profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getSelf]);

  const save = async (patch: Record<string, any>) => {
    const updated = await saveSelf(patch);
    setProfile(updated);
    return updated;
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="tracking-tight text-foreground font-display text-page-title">My profile</h1>
        <p className="text-muted-foreground mt-1">Keep your personal details up to date. Employment details are managed by HR.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading your profile…</div>
      ) : error ? (
        <EmptyState icon={<UserCircle />} title="Profile unavailable" description={error} />
      ) : profile ? (
        <PersonalDetailsForm profile={profile} canEdit includeBasic onSave={save} />
      ) : null}
    </div>
  );
}
